package com.patronat.top.features.auth.presentation

import com.patronat.top.core.base.BaseViewModel
import com.patronat.top.core.domain.usecase.command.auth.*
import com.patronat.top.core.presentation.AlertController
import com.patronat.top.core.presentation.NotificationType
import com.patronat.top.core.utils.UiText
import com.patronat.top.core.validation.Validator
import com.patronat.top.dto.AuthFlow
import com.patronat.top.dto.AuthMethod
import com.patronat.top.dto.UserRole
import com.patronat.top.features.auth.domain.models.AuthResult
import com.patronat.top.features.auth.domain.usecase.*
import io.github.aakira.napier.Napier
import top.features.auth.generated.resources.Res
import top.features.auth.generated.resources.*
import top.design_system.generated.resources.Res as DesignRes
import top.design_system.generated.resources.*

class AuthViewModel(
    private val alertController: AlertController,
    private val startLoginUseCase: StartLoginUseCase,
    private val startRegistrationUseCase: StartRegistrationUseCase,
    private val confirmPhoneUseCase: ClientConfirmPhoneUseCase,
    private val confirmEmailUseCase: ClientConfirmEmailUseCase,
    private val resendPhoneCodeUseCase: ResendPhoneCodeUseCase,
    private val resendEmailCodeUseCase: ResendEmailCodeUseCase
) : BaseViewModel<AuthViewState, AuthEvent, AuthEffect>(
    initialState = AuthViewState()
) {

    private val TAG = "AuthViewModel"

    override fun onEvent(event: AuthEvent) {
        Napier.d(tag = TAG) { "onEvent: $event" }

        when (event) {
            // UI Switch
            is AuthEvent.OnTabSelected -> handleTabChange(event.flow)
            is AuthEvent.OnLoginMethodSelected -> updateState {
                it.copy(loginMethod = event.method, errorMessage = null)
            }

            // Input
            is AuthEvent.OnAuthDataChanged -> updateData(event)

            // Actions
            AuthEvent.OnSubmitLogin -> processLogin()
            AuthEvent.OnSubmitRegister -> processRegistration()
            is AuthEvent.OnSubmitCode -> processCodeEntry(event.code)
            AuthEvent.OnResendCode -> processResend()

            // Navigation
            AuthEvent.OnBackClicked -> handleBack()
            AuthEvent.ClearError -> updateState { it.copy(errorMessage = null) }
        }
    }

    private fun processLogin() {
        val loginValue: String
        val method = currentState.loginMethod

        Napier.d(tag = TAG) { "Starting login process. Method: $method" }

        updateState { it.copy(phoneError = null, emailError = null, errorMessage = null) }

        if (method == AuthMethod.PHONE) {
            if (currentState.phone.isBlank()) {
                Napier.w(tag = TAG) { "Login validation failed: Phone is blank" }
                updateState { it.copy(phoneError = UiText.StringResource(Res.string.error_enter_phone)) }
                return
            }
            if (!Validator.isValidPhone(currentState.phone)) {
                Napier.w(tag = TAG) { "Login validation failed: Invalid phone format ${currentState.phone}" }
                updateState { it.copy(phoneError = UiText.StringResource(Res.string.error_invalid_phone_format)) }
                return
            }
            loginValue = currentState.phone

        } else {
            if (currentState.email.isBlank()) {
                Napier.w(tag = TAG) { "Login validation failed: Email is blank" }
                updateState { it.copy(emailError = UiText.StringResource(Res.string.error_enter_email)) }
                return
            }
            if (!Validator.isValidEmail(currentState.email)) {
                Napier.w(tag = TAG) { "Login validation failed: Invalid email format ${currentState.email}" }
                updateState { it.copy(emailError = UiText.StringResource(Res.string.error_invalid_email_format)) }
                return
            }
            loginValue = currentState.email
        }

        launchNetwork(
            request = {
                Napier.d(tag = TAG) { "Executing StartLoginUseCase..." }
                startLoginUseCase.execute(
                    LoginCommand(login = loginValue, authMethod = method)
                )
            },
            onLoading = { l -> updateState { it.copy(isLoading = l) } },
            onError = { _, msg ->
                Napier.e(tag = TAG) { "Login API Error: $msg" }
                // 🔥 ВИПРАВЛЕНО: Використовуємо UiText для помилки
                val errorText = msg?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.error_login_failed)
                handleError(errorText)
            },
            onSuccess = { result -> handleAuthResult(result) }
        )
    }

    private fun processRegistration() {
        Napier.d(tag = TAG) { "Starting registration process..." }

        val firstNameError =
            if (!Validator.isValidName(currentState.firstName)) UiText.StringResource(Res.string.error_enter_valid_name) else null
        val lastNameError =
            if (!Validator.isValidName(currentState.lastName)) UiText.StringResource(Res.string.error_enter_valid_lastname) else null

        val phoneError = when {
            currentState.phone.isBlank() -> UiText.StringResource(Res.string.error_enter_phone)
            !Validator.isValidPhone(currentState.phone) -> UiText.StringResource(Res.string.error_invalid_phone_format)
            else -> null
        }

        val isEmailRequired = currentState.role != UserRole.CLIENT
        val emailError = when {
            isEmailRequired && currentState.email.isBlank() -> UiText.StringResource(Res.string.error_enter_email)
            currentState.email.isNotBlank() && !Validator.isValidEmail(currentState.email) -> UiText.StringResource(Res.string.error_invalid_email_format)
            else -> null
        }

        val hasErrors = firstNameError != null || lastNameError != null || phoneError != null || emailError != null

        if (hasErrors) {
            Napier.w(tag = TAG) { "Registration validation failed. Errors found." }
            updateState {
                it.copy(
                    firstNameError = firstNameError,
                    lastNameError = lastNameError,
                    phoneError = phoneError,
                    emailError = emailError,
                    errorMessage = UiText.StringResource(Res.string.error_check_data)
                )
            }
            return
        }

        val emailToSend = currentState.email.ifBlank { null }

        launchNetwork(
            request = {
                Napier.d(tag = TAG) { "Executing StartRegistrationUseCase..." }
                startRegistrationUseCase.execute(
                    RegistrationUserCommand(
                        firstName = currentState.firstName,
                        lastName = currentState.lastName,
                        phone = currentState.phone,
                        email = emailToSend,
                        role = currentState.role
                    )
                )
            },
            onLoading = { l -> updateState { it.copy(isLoading = l) } },
            onError = { _, msg ->
                Napier.e(tag = TAG) { "Registration API Error: $msg" }
                // 🔥 ВИПРАВЛЕНО
                val errorText = msg?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.error_registration_failed)
                handleError(errorText)
            },
            onSuccess = { result -> handleAuthResult(result) }
        )
    }

    private fun processCodeEntry(code: String) {
        Napier.d(tag = TAG) { "User entered code for step: ${currentState.step}" }
        updateState { it.copy(errorMessage = null, otpCode = code) }

        when (currentState.step) {
            AuthStep.LoginVerify -> {
                if (currentState.loginMethod == AuthMethod.PHONE) {
                    verifyPhone(code)
                } else {
                    verifyEmail(code)
                }
            }

            AuthStep.RegisterVerifyPhone -> verifyPhone(code)
            AuthStep.RegisterVerifyEmail -> verifyEmail(code)

            else -> {
                Napier.w(tag = TAG) { "Code entered in unexpected state: ${currentState.step}" }
            }
        }
    }

    private fun verifyPhone(code: String) {
        launchNetwork(
            request = {
                Napier.d(tag = TAG) { "Verifying phone code..." }
                confirmPhoneUseCase.execute(
                    ConfirmPhoneCommand(currentState.phone, code, currentState.flow)
                )
            },
            onLoading = { l -> updateState { it.copy(isLoading = l) } },
            onError = { _, msg ->
                Napier.e(tag = TAG) { "Verify Phone Error: $msg" }
                updateState {
                    it.copy(
                        errorMessage = msg?.let { str -> UiText.DynamicString(str) } ?: UiText.StringResource(Res.string.error_invalid_code)
                    )
                }
            },
            onSuccess = { result -> handleAuthResult(result) }
        )
    }

    private fun verifyEmail(code: String) {
        launchNetwork(
            request = {
                Napier.d(tag = TAG) { "Verifying email code..." }
                confirmEmailUseCase.execute(
                    ConfirmEmailCommand(currentState.email, code, currentState.flow)
                )
            },
            onLoading = { l -> updateState { it.copy(isLoading = l) } },
            onError = { _, msg ->
                Napier.e(tag = TAG) { "Verify Email Error: $msg" }
                updateState {
                    it.copy(
                        errorMessage = msg?.let { str -> UiText.DynamicString(str) } ?: UiText.StringResource(Res.string.error_invalid_code)
                    )
                }
            },
            onSuccess = { result -> handleAuthResult(result) }
        )
    }

    private fun processResend() {
        Napier.i(tag = TAG) { "User requested Resend Code" }
        when (currentState.step) {
            AuthStep.LoginVerify -> {
                if (currentState.loginMethod == AuthMethod.PHONE) {
                    resendSms()
                } else {
                    resendEmail()
                }
            }

            AuthStep.RegisterVerifyPhone -> resendSms()
            AuthStep.RegisterVerifyEmail -> resendEmail()

            else -> Unit
        }
    }


    private fun resendSms() {
        if (currentState.phone.isBlank()) return

        launchNetwork(
            request = { resendPhoneCodeUseCase.execute(SendCodeToPhoneCommand(currentState.phone)) },
            onSuccess = { result ->
                Napier.i(tag = TAG) { "SMS resent successfully" }
                sendEffect(AuthEffect.ShowToast(UiText.StringResource(Res.string.toast_sms_resent)))
                handleAuthResult(result)
            },
            onError = { _, msg ->
                Napier.e(tag = TAG) { "Resend SMS Error: $msg" }
                val errorText = if (msg != null) {
                    UiText.DynamicString(msg)
                } else {
                    UiText.StringResource(Res.string.error_sms_send_failed)
                }
                sendEffect(AuthEffect.ShowToast(errorText))
            }
        )
    }

    private fun resendEmail() {
        if (currentState.email.isBlank()) return

        launchNetwork(
            request = { resendEmailCodeUseCase.execute(SendCodeToEmailCommand(currentState.email)) },
            onSuccess = { result ->
                Napier.i(tag = TAG) { "Email resent successfully" }
                sendEffect(AuthEffect.ShowToast(UiText.StringResource(Res.string.toast_email_resent)))
                handleAuthResult(result)
            },
            onError = { _, msg ->
                Napier.e(tag = TAG) { "Resend Email Error: $msg" }
                val errorText = if (msg != null) {
                    UiText.DynamicString(msg)
                } else {
                    UiText.StringResource(Res.string.error_email_send_failed)
                }
                sendEffect(AuthEffect.ShowToast(errorText))
            }
        )
    }

    private fun handleAuthResult(result: AuthResult) {
        Napier.d(tag = TAG) { "handleAuthResult received: $result" }

        when (result) {
            is AuthResult.Success -> {
                Napier.i(tag = TAG) { "Auth Success! Sending NavigateToHome effect with role: ${result.role}" }
                sendEffect(AuthEffect.NavigateToHome(result.role))
            }

            is AuthResult.Continue -> {
                Napier.d(tag = TAG) { "Auth Continue. Next step: ${result.nextStep}, OTP sent: ${result.isOtpSent}" }

                updateState { it.copy(isOtpSent = result.isOtpSent, otpCode = "") }

                val nextUiStep = when (result.nextStep) {
                    AuthMethod.PHONE -> {
                        if (currentState.flow == AuthFlow.LOGIN) {
                            AuthStep.LoginVerify
                        } else {
                            AuthStep.RegisterVerifyPhone
                        }
                    }

                    AuthMethod.EMAIL -> {
                        if (currentState.flow == AuthFlow.LOGIN) {
                            AuthStep.LoginVerify
                        } else {
                            AuthStep.RegisterVerifyEmail
                        }
                    }
                }
                updateState { it.copy(step = nextUiStep) }

                if (!result.isOtpSent) {
                    Napier.i(tag = TAG) { "OTP not sent by server, triggering auto-resend..." }
                    when (result.nextStep) {
                        AuthMethod.PHONE -> resendSms()
                        AuthMethod.EMAIL -> resendEmail()
                    }
                }
            }
        }
    }

    private fun handleTabChange(flow: AuthFlow) {
        Napier.d(tag = TAG) { "Tab changed to: $flow" }
        updateState {
            it.copy(
                flow = flow,
                errorMessage = null,
                step = if (flow == AuthFlow.LOGIN) AuthStep.LoginInput else AuthStep.RegisterInput
            )
        }
    }

    private fun updateData(event: AuthEvent.OnAuthDataChanged) {
        updateState {
            it.copy(
                phone = event.phone ?: it.phone,
                email = event.email ?: it.email,
                firstName = event.firstName ?: it.firstName,
                lastName = event.lastName ?: it.lastName,
                role = event.role ?: it.role,
                errorMessage = null
            )
        }
    }

    private fun handleBack() {
        Napier.d(tag = TAG) { "Handle Back. Current Step: ${currentState.step}" }
        updateState { state ->
            when (state.step) {
                AuthStep.LoginVerify -> state.copy(
                    step = AuthStep.LoginInput,
                    otpCode = "",
                    errorMessage = null
                )

                AuthStep.RegisterVerifyPhone -> state.copy(
                    step = AuthStep.RegisterInput,
                    otpCode = "",
                    errorMessage = null
                )

                AuthStep.RegisterVerifyEmail -> state.copy(
                    step = AuthStep.RegisterVerifyPhone,
                    otpCode = "",
                    errorMessage = null
                )

                else -> state
            }
        }
    }

    private fun handleError(message: UiText) {
        Napier.e(tag = TAG) { "Show Alert Error: $message" }
        alertController.show(
            title = UiText.StringResource(DesignRes.string.error_alert_title),
            message = message,
            type = NotificationType.ERROR
        )
    }
}